// Sound effects and haptic feedback helper for task operations
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'

export type FeedbackType = 'move' | 'complete' | 'create' | 'delete' | 'error' | 'warning' | 'selection'

interface FeedbackSettings {
  soundEnabled: boolean
  hapticEnabled?: boolean
}

// Audio context for generating sounds
let audioContext: AudioContext | null = null

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
  }
  return audioContext
}

// Generate different sounds for different actions
const playSound = async (type: FeedbackType, volume: number = 0.3) => {
  try {
    const ctx = getAudioContext()

    // AudioContext state check - MUST wait for resume
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume()
      } catch (err) {
        console.error('Failed to resume AudioContext:', err)
        return
      }
    }

    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    // Different frequencies and durations for different actions
    // Use lower frequencies (440Hz = A note) for better mobile compatibility
    switch (type) {
      case 'move':
        oscillator.frequency.value = 440 // A note
        oscillator.type = 'sine' // Sine wave for cleaner sound
        gainNode.gain.setValueAtTime(volume * 0.03, ctx.currentTime) // Ultra minimal volume
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.2)
        break

      case 'complete':
        // Double beep for completion
        oscillator.frequency.value = 523 // C note
        oscillator.type = 'sine'
        gainNode.gain.setValueAtTime(volume * 0.03, ctx.currentTime) // Ultra minimal volume
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.15)

        // Second beep
        setTimeout(() => {
          const osc2 = ctx.createOscillator()
          const gain2 = ctx.createGain()
          osc2.connect(gain2)
          gain2.connect(ctx.destination)
          osc2.frequency.value = 659 // E note
          osc2.type = 'sine'
          gain2.gain.setValueAtTime(volume * 0.03, ctx.currentTime) // Ultra minimal volume
          gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2)
          osc2.start(ctx.currentTime)
          osc2.stop(ctx.currentTime + 0.2)
        }, 150)
        break

      case 'create':
        oscillator.frequency.value = 392 // G note
        oscillator.type = 'sine'
        gainNode.gain.setValueAtTime(volume * 0.03, ctx.currentTime) // Ultra minimal volume
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.25)
        break

      case 'delete':
        // Descending tone for delete
        oscillator.frequency.setValueAtTime(523, ctx.currentTime) // C note
        oscillator.frequency.exponentialRampToValueAtTime(262, ctx.currentTime + 0.25) // Lower C
        oscillator.type = 'sine'
        gainNode.gain.setValueAtTime(volume * 0.03, ctx.currentTime) // Ultra minimal volume
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.25)
        break

      case 'error':
        oscillator.frequency.value = 220 // A note (lower octave)
        oscillator.type = 'sine'
        gainNode.gain.setValueAtTime(volume * 0.03, ctx.currentTime) // Ultra minimal volume
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.3)
        break
    }
  } catch (error) {
    console.error('Sound playback error:', error)
  }
}

// Haptic feedback patterns for different actions
const triggerHaptic = async (type: FeedbackType) => {
  if (!Capacitor.isNativePlatform()) return

  try {
    switch (type) {
      case 'move':
      case 'selection':
        await Haptics.impact({ style: ImpactStyle.Light })
        break

      case 'complete':
        await Haptics.notification({ type: NotificationType.Success })
        break

      case 'create':
        await Haptics.impact({ style: ImpactStyle.Medium })
        break

      case 'delete':
        await Haptics.impact({ style: ImpactStyle.Heavy })
        break

      case 'error':
        await Haptics.notification({ type: NotificationType.Error })
        break

      case 'warning':
        await Haptics.notification({ type: NotificationType.Warning })
        break
    }
  } catch (error) {
    console.error('Haptic feedback error:', error)
  }
}

// Haptic-only feedback (for swipe, drag operations)
export const triggerHapticOnly = async (type: FeedbackType) => {
  await triggerHaptic(type)
}

// Main feedback function (sound + haptic)
export const triggerFeedback = async (
  type: FeedbackType,
  settings: FeedbackSettings = { soundEnabled: true, hapticEnabled: true }
) => {
  // Trigger haptic feedback (always enabled on native, controlled by settings)
  if (settings.hapticEnabled !== false) {
    await triggerHaptic(type)
  }

  // Check if sound is enabled
  if (!settings.soundEnabled) {
    return // Exit early if sound is disabled
  }

  // Ensure audio context is initialized and resumed BEFORE playing sound
  await initializeFeedback()
  await playSound(type)
}

// Initialize audio context on user interaction (required by browsers)
export const initializeFeedback = async () => {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      // Resume context if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume().catch(() => {
          // Silently fail - user hasn't interacted yet
        })
      }
    } catch (error) {
      // Silently fail - AudioContext not supported or permission denied
    }
  } else {
    // Try to resume if suspended
    if (audioContext.state === 'suspended') {
      await audioContext.resume().catch(() => {
        // Silently fail - user hasn't interacted yet
      })
    }
  }
}
